import { $themeBreakpoints } from '@themeConfig';
import api from '@/libs/services/api';
import { BRANCH_ID, CURRENT_YEAR } from '@/libs/acl/config';
import {
  getAccountsEnabledFromStorage,
  setAccountsEnabledInStorage,
  clearAccountsEnabledStorage,
  normalizeAccountsEnabled,
} from '@/helpers/accountsEnabled';

export default {
  namespaced: true,
  state: {
    windowWidth: 0,
    shallShowOverlay: false,
    branches: [],
    fiscalYears: [],
    branchId: window.localStorage.getItem(BRANCH_ID) || 0,
    userCurrentYear: JSON.parse(window.localStorage.getItem(CURRENT_YEAR)) || 0,
    currentBranch: {},
    currentYear: JSON.parse(window.localStorage.getItem(CURRENT_YEAR)) || {},
    currentCompany: {},
    overlay: false,
    postOverlay: false,
    postCancelOverlay: false,
    // Prefer stored value; unknown defaults false so Accounts stays hidden until gl-status confirms
    accountsEnabled: (() => {
      const stored = getAccountsEnabledFromStorage();
      if (stored !== null) return setAccountsEnabledInStorage(stored);
      return false;
    })(),
  },
  getters: {
    overlay: (state) => state.overlay,
    postOverlay: (state) => state.postOverlay,
    postCancelOverlay: (state) => state.postCancelOverlay,
    branches: (state) => state.branches,
    fiscalYears: (state) => state.fiscalYears,
    branchId: (state) => state.branchId || state.branches[0]?.id,
    currentBranch: (state) => state.currentBranch,
    userCurrentYear: (state) => state.userCurrentYear,
    currentYear: (state) => state.currentYear,
    currentCompany: (state) => state.currentCompany,
    accountsEnabled: (state) => state.accountsEnabled,
    currentBreakPoint: (state) => {
      const { windowWidth } = state;
      if (windowWidth >= $themeBreakpoints.xl) return 'xl';
      if (windowWidth >= $themeBreakpoints.lg) return 'lg';
      if (windowWidth >= $themeBreakpoints.md) return 'md';
      if (windowWidth >= $themeBreakpoints.sm) return 'sm';
      return 'xs';
    },
  },
  mutations: {
    UPDATE_WINDOW_WIDTH(state, val) {
      state.windowWidth = val;
    },
    TOGGLE_OVERLAY(state, val) {
      // state.shallShowOverlay = val !== undefined ? val : !state.shallShowOverlay;
      state.overlay = val !== undefined ? val : !state.overlay;
    },
    TOGGLE_POST_OVERLAY(state, val) {
      // state.shallShowOverlay = val !== undefined ? val : !state.shallShowOverlay;
      state.postOverlay = val !== undefined ? val : !state.postOverlay;
    },
    TOGGLE_CANCEL_POST_OVERLAY(state, val) {
      // state.shallShowOverlay = val !== undefined ? val : !state.shallShowOverlay;
      state.postCancelOverlay = val !== undefined ? val : !state.postCancelOverlay;
    },
    setCurrentYear(state, year) {
      state.currentYear = year;
      window.localStorage.setItem(CURRENT_YEAR, JSON.stringify(state.currentYear));
    },
    setCurrentBranch(state, branch) {
      state.currentBranch = branch;
      state.branchId = branch.id;
      window.localStorage.setItem(BRANCH_ID, branch.id);
    },
    setCompany(state, company) {
      state.currentCompany = company;
      window.localStorage.COMAPNY_LOGO = state.currentCompany?.logoUrl;
    },
    SET_ACCOUNTS_ENABLED(state, value) {
      state.accountsEnabled = setAccountsEnabledInStorage(value);
    },
    CLEAR_ACCOUNTS_ENABLED(state) {
      state.accountsEnabled = false;
      clearAccountsEnabledStorage();
    },
  },
  actions: {
    fetchAccountsEnabled({ commit, dispatch }) {
      return dispatch('get', { url: 'licensing/gl-status', showOverLay: false })
        .then((data) => {
          commit('SET_ACCOUNTS_ENABLED', normalizeAccountsEnabled(data));
        })
        .catch(() => {});
    },
    setYears({ rootGetters, state }, fiscalYears) {
      const profile = rootGetters['auth/profile'];
        const currentCalendarYear = new Date().getFullYear();
      state.fiscalYears = fiscalYears;
      if (JSON.stringify(state.userCurrentYear) === '{}') {
        state.userCurrentYear = { ...(state.fiscalYears.find((item) => item.year === new Date().getFullYear())) }
      }
      if (profile.isAdmin) {
        state.currentYear = { ...(state.fiscalYears.find((item) => item.year === state.userCurrentYear.year)) };
      }
      if (profile.isAdmin === false && profile.permissions.indexOf('viewFiscalYears') > -1) {
        state.currentYear = { ...(state.fiscalYears.find((item) => item.year === state.userCurrentYear.year)) };
      }
      if (profile.isAdmin === false && profile.permissions.indexOf('viewFiscalYears') === -1) {
        state.currentYear = state.fiscalYears[state.fiscalYears.length + 1] || {
          year: currentCalendarYear,
          startDate: `${currentCalendarYear}-01-01T00:00:00`,
          endDate: `${currentCalendarYear}-12-31T00:00:00`,
        };
      }
      state.userCurrentYear = state.currentYear;
      window.localStorage.setItem(CURRENT_YEAR, JSON.stringify(state.currentYear));
    },
    setBranches({ rootGetters, state }, branches) {
      const profile = rootGetters['auth/profile'];
      const activeBranches = (branches || []).filter((branch) => branch.isActive !== false);
      const res = activeBranches.filter((x) => profile.branches?.some((b) => b === x.id));
      state.branches = res?.length > 0 ? res : activeBranches;
      const branchId = state.branchId || window.localStorage.getItem(BRANCH_ID);
      const currentBranch = branchId && state.branches.find((b) => b.id === branchId)
          ? state.branches.find((b) => b.id === branchId)
          : state.branches[0];

      if (currentBranch) {
        state.currentBranch = { ...currentBranch };
        state.branchId = currentBranch.id;
        window.localStorage.setItem(BRANCH_ID, state.branchId);
      } else {
        state.currentBranch = {};
        state.branchId = null;
        window.localStorage.removeItem(BRANCH_ID);
      }
      window.localStorage.setItem('COMAPNY_LOGO', state.currentCompany?.logoUrl || '');
    },
    getAll({ commit }, { endpoints, callback }) {
      commit('TOGGLE_OVERLAY', true);
      Promise.all(endpoints.map((endpoint) => api.app.get(endpoint))).then(
        (data) => {
          commit('TOGGLE_OVERLAY', false);
          data = data.map((item) => item.data);
          callback(data);
        }
      ).finally(() => {
        commit('TOGGLE_OVERLAY', false);
      });
    },
    get({ commit }, { id, url, showOverLay }) {
      if (showOverLay === false) {
        commit('TOGGLE_OVERLAY', false);
      } else {
        commit('TOGGLE_OVERLAY', true);
      }
      return new Promise((resolve, reject) => {
        api.app
          .get(url, id)
          .then(({ data }) => {
            resolve(data);
            commit('TOGGLE_OVERLAY', false);
          })
          .catch((error) => {
            reject(error);
            commit('TOGGLE_OVERLAY', false);
          })
          .finally(() => {
            commit('TOGGLE_OVERLAY', false);
          });
      });
    },
    create({ commit }, { url, data }) {
      if (url === 'Posting/post') {
        commit('TOGGLE_POST_OVERLAY', true);
      } else if (url === 'Posting/unpost') {
        commit('TOGGLE_CANCEL_POST_OVERLAY', true);
      } else {
        commit('TOGGLE_OVERLAY', true);
      }
      return new Promise((resolve, reject) => {
        api.app
          .create(url, data)
          .then((res) => {
            resolve(res.data);
            sessionStorage.removeItem('current-idempotency-key');
            commit('TOGGLE_OVERLAY', false);
            commit('TOGGLE_CANCEL_POST_OVERLAY', false);
            commit('TOGGLE_POST_OVERLAY', false);
          })
          .catch(({ response }) => {
            reject(response);
            commit('TOGGLE_CANCEL_POST_OVERLAY', false);
            commit('TOGGLE_POST_OVERLAY', false);
            commit('TOGGLE_OVERLAY', false);
          })
          .finally(() => {
            commit('TOGGLE_CANCEL_POST_OVERLAY', false);
            commit('TOGGLE_POST_OVERLAY', false);
            commit('TOGGLE_OVERLAY', false);
          });
      });
    },
    update({ commit }, { url, data, id }) {
      commit('TOGGLE_OVERLAY', true);
      return new Promise((resolve, reject) => {
        api.app
          .update(url, id, data)
          .then((res) => {
            resolve(res);
            commit('TOGGLE_OVERLAY', false);
          })
          .catch(({ response }) => {
            reject(response);
            commit('TOGGLE_OVERLAY', false);
          })
          .finally(() => {
            commit('TOGGLE_OVERLAY', false);
          });
      });
    },
    delete({ commit }, { url, id, data }) {
      commit('TOGGLE_OVERLAY', true);
      return new Promise((resolved, reject) => {
        api.app
          .delete(url, id, data)
          .then(() => {
            resolved();
            commit('TOGGLE_OVERLAY', false);
          })
          .catch(({ response }) => {
            reject(response);
            commit('TOGGLE_OVERLAY', false);
          })
          .finally(() => {
            commit('TOGGLE_OVERLAY', false);
          });
      });
    },
  },
};
